import {
  AddSeatsModalConfirmed,
  ApplePayButtonClicked,
  ApplePaySetupFailed,
  BaseTrackingAdapter,
  CancellationSurveySubmitted,
  CheckoutFlowCompleted,
  CheckoutFlowFailed,
  CheckoutFlowIssue,
  CheckoutFlowLoaded,
  CheckoutFlowLoadError,
  CheckoutFlowPaymentCompleted,
  ContextMenuActed,
  ContextMenuOpened,
  DeeplinkAutoOpenToggled,
  DeeplinkButtonClicked,
  DeeplinkButtonShown,
  DeeplinkGoToDownload,
  DeeplinkModalClosed,
  DeeplinkModalOpened,
  DeeplinkOpenInitiated,
  DeeplinkRetryButtonClicked,
  ElementClicked,
  ErrorShown,
  getCookie,
  GoToCollaborationPromo,
  GoToDashboard,
  GoToMembers,
  GoToReviewLinks,
  GoToSharedWithMe,
  GoToSpace,
  GoToTeam,
  IntentPageFinished,
  IntentPageLoaded,
  InviteModalClosed,
  InviteModalEmailAdded,
  InviteModalEmailRemoved,
  InviteModalEstimateReceived,
  InviteModalInvalidEmailAdded,
  InviteModalShown,
  InviteModalSubmitted,
  ModalClosed,
  ModalShown,
  PageVisited,
  PaywallModalDismissed,
  PaywallModalShown,
  PaywallSubscribeClicked,
  PriceFetched,
  PriceShown,
  type ProgressiveProfilingCompleted,
  type ProgressiveProfilingStepCreatorsCompleted,
  type ProgressiveProfilingStepDesignStorageCompleted,
  type ProgressiveProfilingStepIndustryCompleted,
  type ProgressiveProfilingStepMainCadCompleted,
  type ProgressiveProfilingStepRoleCompleted,
  type ProgressiveProfilingStepViewersCompleted,
  ProjectPageAccessRequestAccepted,
  ProjectPageAccessRequested,
  ProjectPageAccessRequestRejected,
  ProjectPageAccessSwitchUser,
  ProjectPageDownloadFileClicked,
  ProjectPageDownloadFileShown,
  ProjectPageFeatureTourClose,
  ProjectPageFeatureTourCompleted,
  ProjectPageFeatureTourStepShown,
  ProjectPageLoaded,
  ProjectSidebarToggled,
  RenewalTooltipShown,
  RequestVerificationEmailClicked,
  SegmentationSurveyCompletedV3,
  SegmentationSurveyStep3dProficiencyCompleted,
  SegmentationSurveyStepDesignIntentCompleted,
  SegmentationSurveyStepPrimaryCadCompleted,
  TelemetryClient,
  TwoFactorDisableModalAct,
  UpdatePaymentSourceSuccess,
  UrlChanged,
  UserLoggedIn,
  UserSignedUp,
  VersionSelectorVisibilityChanged,
  VideoStarted,
  VideoStopped,
  WebDashboardTeamSwitcherClicked,
  WebDashboardTeamSwitcherItemClicked,
  WebLinkClicked,
  WebLinkContextMenuActed,
  WebLinkContextMenuOpened,
  WebLinkCopied,
  WebLinkUnpublishConfirmed,
  WebShareModalEmailAdded,
  WebShareModalEmailRemoved,
  WebShareModalInvalidEmailAdded,
  WebShareModalInvitePressed,
  WebShareModalInviteRoleChanged,
  WebShareModalMemberRemoveConfirmed,
  WebShareModalMemberRemoveIntended,
  WebShareModalMemberRoleChanged,
  WebShareModalVisibilityChanged,
  WebViewerActiveToolChanged,
  WebViewerAnnotationPlaced,
  WebViewerCommentAdded,
  WebViewerCommentContextMenuVisibilityChanged,
  WebViewerCommentDeleteConfirmed,
  WebViewerCommentDeleteIntended,
  WebViewerCommentingDisplayed,
  WebViewerCommentingSidebarVisibilityChanged,
  WebViewerCommentSelected,
  WebViewerComplexModelWarningShown,
  WebViewerDrawingDisplayed,
  WebViewerDrawingDisplayFailed,
  WebViewerDrawingDownloaded,
  WebViewerDrawingDownloadFailed,
  WebViewerModelDisplayed,
  WebViewerModelDisplayFailed,
  WebViewerModelDownloaded,
  WebViewerModelDownloadFailed,
  WebViewerModelDownloadStarted,
  WebViewerShareModalGeneralPermissionChanged,
  WebViewerShareModalGeneralRoleChanged,
  WebViewerViewLinkAccessDenied,
  WelcomeModalClosed,
  WelcomeModalShown,
} from "@shapr3d/frontend-lib";

import { telemetryClientEnvironment, telemetryClientUrlOverride } from "../config";
import { SubscriptionTier } from "../types";
import { version } from "../version";
import type { ChargebeePaymentType } from "./ChargebeeAdapter";
import type { PaddleBillingPaymentType } from "./PaddleBillingAdapter";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: Record<string, any>[];
  }
}

interface GTMEvent {
  eventAction?: string;
  eventLabel?: string;
  eventValue?: string;
}

interface GAEvent extends GTMEvent {
  event: string;
  eventCategory?: string;
  clientIdNonAdvertising?: string;
  clientIdAdvertising?: string;
}

export const PaymentPlatforms = ["paddle", "chargebee", "paddle_billing"] as const;
export type PaymentPlatform = (typeof PaymentPlatforms)[number];

/** Values sent on `checkout_flow_loaded.tier` (Individual → solo; Lite/Pro → lite). */
export type CheckoutFlowLoadedNormalizedTier = "lite" | "solo" | "studio";

export function subscriptionTierToCheckoutFlowLoadedTier(
  tier: SubscriptionTier | null | undefined
): CheckoutFlowLoadedNormalizedTier | undefined {
  if (tier == null) {
    return undefined;
  }
  switch (tier) {
    case SubscriptionTier.Individual:
      return "solo";
    case SubscriptionTier.Studio:
      return "studio";
    case SubscriptionTier.Lite:
    case SubscriptionTier.Pro:
      return "lite";
    default: {
      const _exhaustive: never = tier;
      return _exhaustive;
    }
  }
}

export type CheckoutFlowIssueType = CheckoutFlowIssue["eventProperties"]["type"];
export type CheckoutFlowFailedReason = CheckoutFlowFailed["eventProperties"]["reason"];
export type CheckoutFlowLoadErrorReason = CheckoutFlowLoadError["eventProperties"]["reason"];

export enum ElementClickAction {
  ADD_BILLING_DETAILS = "add_billing_details",
  ADD_COUPON_CODE = "add_coupon_code",
  ADD_SEATS = "add_seats",
  ADD_TO_SPACE = "add_to_space",
  APP_STORE_CHARGEBEE_TERMS_TOGGLE = "app_store_chargebee_terms_toggle",
  AUTO_RENEWAL_CANCEL_SUBSCRIPTION_CONFIRMATION = "auto_renewal_cancel_subscription_confirmation",
  CANCEL = "cancel",
  CANCEL_SEATS = "cancel_seats",
  CANCEL_SUBSCRIPTION = "cancel_subscription",
  CONTINUE = "continue",
  CHANGE_BILLING_PERIOD = "change_billing_period",
  CHANGE_EMAIL = "change_email",
  CHANGE_MEMBERS_FILTER = "change_members_filter",
  CHANGE_NAME = "change_name",
  CHANGE_NUMBER_OF_SEATS = "change_number_of_seats",
  CHANGE_PASSWORD = "change_password",
  CHANGE_TO_TEAM = "change_to_team",
  CLEAR_MEMBERS_FILTER = "clear_members_filter",
  CONFIRM = "confirm",
  CREATE_SPACE = "create_space",
  DELETE_ACCOUNT_MODAL_OPENED = "delete_account_modal_opened",
  DESELECT_MEMBERS = "deselect_members",
  EDIT_BILLING_DETAILS = "edit_billing_details",
  EDIT_PAYMENT_INFORMATION = "edit_payment_information",
  FOCUS_MEMBERS_FILTER = "focus_members_filter",
  FORM_SUBMIT_ATTEMPT_WITH_ENTER = "form_submit_attempt_with_enter",
  GO_TO_ACCOUNT_SETTINGS = "go_to_account_settings",
  GO_TO_COMMUNITY = "go_to_community",
  GO_TO_CONTACT_SALES = "go_to_contact_sales",
  GO_TO_CONTACT_US = "go_to_contact_us",
  GO_TO_CONTACT_SUPPORT = "go_to_contact_support",
  GO_TO_DOWNLOAD = "go_to_download",
  GO_TO_HELP_CENTER = "go_to_help_center",
  GO_TO_LEARN_MORE = "go_to_learn_more",
  GO_TO_TEAMS_MARKETING = "go_to_teams_marketing",
  GO_TO_VIDEO_TUTORIALS = "go_to_video_tutorials",
  INVITE_MEMBERS = "invite_members",
  INVITE_WEB_MEMBERS = "invite_web_members",
  JOIN_TEAM = "join_team",
  LOG_OUT = "log_out",
  LEARN_MORE_PUBLISHING_VERSIONS = "learn_more_publishing_versions",
  LINK_PERSONAL_ACCOUNT = "link_personal_account",
  LINK_PERSONAL_ACCOUNT_CONFIRM = "link_personal_account_confirm",
  MANAGE_AUTO_RENEWAL = "manage_auto_renewal",
  MANAGE_SEATS = "manage_seats",
  MANAGE_SEATS_ADD_SEATS_TO_TRIAL = "manage_seats_add_seats_to_trial",
  MANAGE_SEATS_BUY_SEATS = "manage_seats_buy_seats",
  MONTHLY_YEARLY_TOGGLE = "monthly_yearly_toggle",
  OPEN_MEMBER_OPTIONS_MENU = "open_member_options_menu",
  OPEN_SHAPR = "open_shapr",
  OPEN_SPACE_OPTIONS_MENU = "open_space_options_menu",
  OPEN_TEAM_SWITCHER = "open_team_switcher",
  OPEN_TEAMS_VIDEO = "open_teams_video",
  OPEN_TOP_RIGHT_MENU = "open_top_right_menu",
  PURCHASE_NOTICE_COMPARE_SUBSCRIPTIONS = "purchase_notice_compare_subscriptions",
  PURCHASE_NOTICE_START_TRIAL = "purchase_notice_start_trial",
  PURCHASE_NOTICE_SUBSCRIBE_NOW = "purchase_notice_subscribe_now",
  REMOVE_FROM_SPACE = "remove_from_space",
  REMOVE_INVITATION = "remove_invitation",
  REMOVE_MEMBER = "remove_member",
  RENAME_SPACE = "rename_space",
  SEAT_COUNT_CHANGE = "seat_count_change",
  SELECT_MEMBERS = "select_members",
  SELECT_ROLE = "select_role",
  SELECT_SPACE = "select_space",
  START_EDU_FLOW = "start_edu_flow",
  START_SIGNUP_FLOW = "start_signup_flow",
  START_TRIAL_FLOW = "start_trial_flow",
  SUBSCRIBE_NOW_SOLO = "subscribe_now_solo",
  SUBSCRIBE_NOW_STUDIO = "subscribe_now_studio",
  SUBMIT_CHECKOUT_FORM = "submit_checkout_form",
  SUBMIT_CHECKOUT_FORM_TRIAL = "submit_checkout_form_trial",
  SUBSCRIPTION_PERIOD_PICK = "subscription_period_pick",
  TEAM_NAME_EDIT_CHANGE = "team_name_edit_change",
  TURN_ON_AUTO_RENEWAL = "turn_on_auto_renewal",
  UPDATE_PAYMENT_INFORMATION = "update_payment_information",
  UPDATE_TEAM_NAME = "update_team_name",
  UPDATE_TEAM_NAME_SAVE = "update_team_name_save",
  UPGRADE_TO_PRO = "upgrade_to_pro",
  UPGRADE_BUTTON = "upgrade_button",
  UPGRADE_MODAL_CONFIRMED = "upgrade_modal_confirmed",
  //
  OPEN_ACCOUNT_MENU = "open_account_menu",
  CLOSE_ACCOUNT_MENU = "close_account_menu",
  DOWNLOAD_PDF = "download-pdf",
  NAVBAR_OPEN = "navbar_open",
  NAVBAR_CLOSE = "navbar_close",
  COPY_LINK = "copy_link",
  LOGIN = "login",
  PUBLISH_VERSION = "publish_version",
}

export enum ElementClickLocation {
  ADD_SEATS_MODAL = "add_seats_modal",
  PRICE_CARD_SECTION = "price_card_section",
  ADD_TO_SPACE_MODAL = "add_to_space_modal",
  AUTO_RENEWAL_MANAGE_MENU = "auto_renewal_manage_menu",
  CANCEL_SEATS_MODAL = "cancel_seats_modal",
  CANCELLATION_SURVEY_MODAL = "cancellation_survey_modal",
  CANCEL_SUBSCRIPTION_MODAL = "cancel_subscription_modal",
  CHANGE_EMAIL_MODAL = "change_email_modal",
  CHANGE_NAME_MODAL = "change_name_modal",
  CHANGE_PASSWORD_MODAL = "change_password_modal",
  CHECKOUT = "checkout",
  EDIT_BILLING_DETAILS_MODAL = "edit_billing_details_modal",
  EDIT_PAYMENT_INFORMATION_MODAL = "edit_payment_information_modal",
  HERO = "hero",
  INVITE_MEMBERS_MODAL = "invite_members_modal",
  MANAGE_SEATS_MENU = "manage_seats_menu",
  MEMBER_OPTIONS_MENU = "member_options_menu",
  MEMBERS_TABLE_FILTER_ROW = "members_table_filter_row",
  MEMBERS_TABLE_HEADER = "members_table_header",
  PROJECT_PAGE = "project_page",
  REMOVE_FROM_SPACE_MODAL = "remove_from_space_modal",
  REMOVE_MEMBER_MODAL = "remove_member_modal",
  SPACE_CREATION_MODAL = "space_creation_modal",
  SPACE_OPTIONS_MENU = "space_options_menu",
  SPACE_RENAME_MODAL = "space_rename_modal",
  TEAM_SUBSCRIPTION_PAGE = "team_subscription_page",
  TEAM_SWITCHER_MENU = "team_switcher_menu",
  TIER_UPGRADE_MODAL = "tier_upgrade_modal",
  TOP_RIGHT_MENU = "top_right_menu",
  WELCOME_MODAL = "welcome_modal",
  UPDATE_TEAM_NAME_MODAL = "update_team_name_modal",
  VIDEO_CAPTION = "video_caption",
  //
  SHARE_MODAL = "share_modal",
  HEADER = "header",
  CHECKOUT_SUCCESS_NON_RECURRING = "checkout_success_non_recurring",
  UNPUBLISHED_CHANGES_BANNER = "unpublished_changes_banner",
}

export enum ModalShownAction {
  TIER_UPGRADE_MODAL_SHOWN = "tier_upgrade_modal_shown",
  TWO_FACTOR_ENABLE_INTENT = "two_factor_enable_intent",
  TWO_FACTOR_SETUP_SUCCESS = "two_factor_setup_success",
  TWO_FACTOR_RECOVERY_CODE = "two_factor_recovery_code",
  TWO_FACTOR_DISABLE = "two_factor_disable",
  ADD_SEAT_UNAVAILABLE = "add_seat_unavailable",
  APPLE_PAY_CONFIRMATION = "apple_pay_confirmation",
  INVITE_UNAVAILABLE = "invite_unavailable",
  PUBLISH_VERSION_MODAL = "publish_version_modal",
  CANCELLATION_SURVEY = "cancellation_survey",
  CANCEL_SUBSCRIPTION = "cancel_subscription",
}

export enum ModalClosedAction {
  TIER_UPGRADE_MODAL_CLOSED = "tier_upgrade_modal_closed",
  TWO_FACTOR_ENABLE_INTENT = "two_factor_enable_intent",
  TWO_FACTOR_SETUP_SUCCESS = "two_factor_setup_success",
  TWO_FACTOR_RECOVERY_CODE = "two_factor_recovery_code",
  TWO_FACTOR_DISABLE = "two_factor_disable",
  PUBLISH_VERSION_MODAL = "publish_version_modal",
  CANCELLATION_SURVEY = "cancellation_survey",
  CANCEL_SUBSCRIPTION = "cancel_subscription",
}

export default class TrackingAdapter extends BaseTrackingAdapter {
  constructor(telemetryClient: TelemetryClient) {
    super(telemetryClient);
  }

  setUserData({ userID }: { userID: string | null }) {
    this.telemetryClient.setUserId(userID);
  }

  setLocale(locale: string | null) {
    this.telemetryClient.setLocale(locale);
  }

  reset() {
    this.setUserData({ userID: null });
    this.telemetryClient.resetTrackingID();
  }

  private sendGAEvent(gaEvent: GAEvent) {
    if (window.dataLayer) {
      window.dataLayer.push(gaEvent);
    }
  }

  trackURLChanged = this.createTelemetryTrackingFunction(UrlChanged);

  trackPageView() {
    this.telemetryClient.trackTelemetryEvent(new PageVisited({ referrer: document.referrer }));
  }

  trackButtonClick({
    elementId,
    action,
    location,
    details,
  }: {
    /**
     * @deprecated
     */
    elementId?: string;
    action?: ElementClickAction;
    location?: string;
    details?: string;
  }) {
    this.telemetryClient.trackTelemetryEvent(
      new ElementClicked({ elementId, action, location, details })
    );
  }

  trackPriceFetched(duration: number) {
    this.telemetryClient.trackTelemetryEvent(new PriceFetched({ durationMs: duration }));
  }

  trackLoggedIn(appTrackingId?: string) {
    this.telemetryClient.trackTelemetryEvent(new UserLoggedIn({ appTrackingId }));
  }

  private getMarketingCookies() {
    const marketingCookies: Record<string, string | undefined> = {};

    const MARKETING_URL_KEYS = ["msclkid", "gclid"] as const;
    const searchParams = new URLSearchParams(window.location.search);
    MARKETING_URL_KEYS.forEach((urlKey) => {
      const cookieKey = "_" + urlKey;

      const value = searchParams.get(urlKey);
      if (value) {
        marketingCookies[cookieKey] = value;
      }
    });

    const MARKETING_COOKIES = ["_msclkid", "_gclid", "_fbp", "_fbc", "_ga"] as const;
    MARKETING_COOKIES.forEach((cookieName) => {
      const value = getCookie(cookieName);
      if (value) {
        marketingCookies[cookieName] = value;
      }
    });

    return marketingCookies;
  }

  trackSignedUp(appTrackingId?: string) {
    const marketingCookies = this.getMarketingCookies();

    this.telemetryClient.trackTelemetryEvent(
      new UserSignedUp({
        type: "email",
        appTrackingId,
        ...(Object.keys(marketingCookies).length > 0 && { marketingCookies }),
      })
    );
    this.sendGAEvent({
      event: "GAEvent",
      eventCategory: "App signup web",
      eventAction: "Account creation sign up web",
      eventLabel: window.location.href,
    });
  }

  trackAutoRenewalTooltipOpened() {
    this.telemetryClient.trackTelemetryEvent(new RenewalTooltipShown());
  }

  trackCheckoutAbandonment() {
    this.sendGAEvent({
      event: "GAEvent",
      eventCategory: "Checkout",
      eventAction: "Abandon",
    });
  }

  trackInviteSuccess() {
    this.sendGAEvent({
      event: "GAEvent",
      eventCategory: "Invite modal",
      eventAction: "Success",
    });
  }

  trackInviteAbandonment(properties: ConstructorParameters<typeof InviteModalClosed>[0]) {
    this.telemetryClient.trackTelemetryEvent(new InviteModalClosed(properties));

    this.sendGAEvent({
      event: "GAEvent",
      eventCategory: "Invite modal",
      eventAction: "Abandon",
    });
  }

  trackPricingClickFromPricingPage() {
    this.sendGAEvent({
      event: "GAEvent",
      eventCategory: "Checkout",
      eventAction: "Initiate",
      eventLabel: "Buy now click - Pricing",
      eventValue: undefined,
    });
  }

  trackClientIDs(event: MessageEvent) {
    try {
      const analyticsData = JSON.parse(event.data);

      if (!analyticsData?.nonAdvertising || !analyticsData?.advertising) {
        return;
      }

      this.sendGAEvent({
        event: "clientIdDelivered",
        clientIdNonAdvertising: analyticsData.nonAdvertising.clientId,
        clientIdAdvertising: analyticsData.advertising.clientId,
      });
    } catch (err) {
      console.error(err);
    }
  }

  trackProgressiveProfilingEvent(
    event:
      | ProgressiveProfilingStepCreatorsCompleted
      | ProgressiveProfilingStepDesignStorageCompleted
      | ProgressiveProfilingStepIndustryCompleted
      | ProgressiveProfilingStepMainCadCompleted
      | ProgressiveProfilingStepRoleCompleted
      | ProgressiveProfilingStepViewersCompleted
      | ProgressiveProfilingCompleted
  ) {
    this.telemetryClient.trackTelemetryEvent(event);
  }

  trackSegmentationSurveyV4Events(
    events: (
      | SegmentationSurveyCompletedV3
      | SegmentationSurveyStep3dProficiencyCompleted
      | SegmentationSurveyStepDesignIntentCompleted
      | SegmentationSurveyStepPrimaryCadCompleted
    )[]
  ) {
    this.telemetryClient.trackTelemetryEvents(events);
  }

  trackCheckoutFlowLoaded({
    paymentPlatform,
    trial,
    subscriptionTier,
  }: {
    paymentPlatform: PaymentPlatform;
    trial?: boolean;
    subscriptionTier?: SubscriptionTier | null;
  }) {
    const tier = subscriptionTierToCheckoutFlowLoadedTier(subscriptionTier);
    this.telemetryClient.trackTelemetryEvent(
      new CheckoutFlowLoaded({ paymentPlatform, trial, tier })
    );
  }

  trackCheckoutFlowLoadError(
    reason: CheckoutFlowLoadErrorReason,
    paymentPlatform?: "chargebee" | "paddle_billing"
  ) {
    this.telemetryClient.trackTelemetryEvent(
      new CheckoutFlowLoadError({ reason, paymentPlatform })
    );
  }

  trackCheckoutFlowCompleted(
    isTrial: boolean,
    platform: PaymentPlatform,
    seats: number,
    paymentType: ChargebeePaymentType | PaddleBillingPaymentType | undefined
  ) {
    this.sendGAEvent({
      event: "GAEvent",
      eventCategory: "Checkout",
      eventAction: "Payment Complete",
      eventValue: undefined,
    });
    this.telemetryClient.trackTelemetryEvent(
      new CheckoutFlowCompleted({ trial: isTrial, paymentPlatform: platform, seats, paymentType })
    );
  }

  trackCheckoutFlowIssue(isTrial: boolean, issueType: CheckoutFlowIssueType) {
    this.telemetryClient.trackTelemetryEvent(
      new CheckoutFlowIssue({
        trial: isTrial,
        paymentPlatform: "chargebee",
        type: issueType,
      })
    );
  }

  trackCheckoutFlowFailed = this.createTelemetryTrackingFunction(CheckoutFlowFailed);

  trackCheckoutFlowPaymentCompleted() {
    this.telemetryClient.trackTelemetryEvent(
      new CheckoutFlowPaymentCompleted({ paymentPlatform: "paddle_billing" })
    );
  }

  trackAddSeatsModalConfirmed(...props: ConstructorParameters<typeof AddSeatsModalConfirmed>) {
    this.telemetryClient.trackTelemetryEvent(new AddSeatsModalConfirmed(...props));
  }

  trackInviteModalEmailAdded = this.createTelemetryTrackingFunction(InviteModalEmailAdded);

  trackInviteModalInvalidEmailAdded = this.createTelemetryTrackingFunction(
    InviteModalInvalidEmailAdded
  );

  trackInviteModalEmailRemoved = this.createTelemetryTrackingFunction(InviteModalEmailRemoved);

  trackInviteModalSubmitted = this.createTelemetryTrackingFunction(InviteModalSubmitted);

  trackInviteModalError(details?: string) {
    this.telemetryClient.trackTelemetryEvent(
      new ErrorShown({
        action: ElementClickAction.INVITE_MEMBERS,
        location: ElementClickLocation.INVITE_MEMBERS_MODAL,
        details,
      })
    );
  }

  trackEmailChangeModalError(error?: unknown) {
    this.telemetryClient.trackTelemetryEvent(
      new ErrorShown({
        action: ElementClickAction.CHANGE_EMAIL,
        location: ElementClickLocation.CHANGE_EMAIL_MODAL,
        details: JSON.stringify(error),
      })
    );
  }

  trackNameChangeModalError(error?: string) {
    this.telemetryClient.trackTelemetryEvent(
      new ErrorShown({
        action: ElementClickAction.CHANGE_NAME,
        location: ElementClickLocation.CHANGE_NAME_MODAL,
        details: error,
      })
    );
  }

  trackModalShown(action: ModalShownAction, details?: string, location?: string) {
    this.telemetryClient.trackTelemetryEvent(
      new ModalShown({
        action,
        details,
        location,
      })
    );
  }

  trackModalClosed(action: ModalClosedAction, details?: string, location?: string) {
    this.telemetryClient.trackTelemetryEvent(
      new ModalClosed({
        action,
        details,
        location,
      })
    );
  }

  trackCancellationSurveySubmitted = this.createTelemetryTrackingFunction(
    CancellationSurveySubmitted
  );

  trackRequestVerificationEmailClicked = this.createTelemetryTrackingFunction(
    RequestVerificationEmailClicked
  );
  trackTwoFactorDisableAct = this.createTelemetryTrackingFunction(TwoFactorDisableModalAct);

  trackInviteModalShown = this.createTelemetryTrackingFunction(InviteModalShown);
  trackInviteModalEstimateReceived = this.createTelemetryTrackingFunction(
    InviteModalEstimateReceived
  );
  trackGoToReviewLinks = this.createTelemetryTrackingFunction(GoToReviewLinks);
  trackGoToSharedWithMe = this.createTelemetryTrackingFunction(GoToSharedWithMe);
  trackApplePayButtonClicked = this.createTelemetryTrackingFunction(ApplePayButtonClicked);
  trackUpdatePaymentSourceSuccess = this.createTelemetryTrackingFunction(
    UpdatePaymentSourceSuccess
  );
  trackApplePaySetupFailed = this.createTelemetryTrackingFunction(ApplePaySetupFailed);

  //

  trackModelDisplayed = this.createTelemetryTrackingFunction(WebViewerModelDisplayed);
  trackModelDisplayFailed = this.createTelemetryTrackingFunction(WebViewerModelDisplayFailed);
  trackModelDownloadStarted = this.createTelemetryTrackingFunction(WebViewerModelDownloadStarted);
  trackModelDownloaded = this.createTelemetryTrackingFunction(WebViewerModelDownloaded);
  trackModelDownloadFailed = this.createTelemetryTrackingFunction(WebViewerModelDownloadFailed);
  trackDrawingDisplayed = this.createTelemetryTrackingFunction(WebViewerDrawingDisplayed);
  trackDrawingDisplayFailed = this.createTelemetryTrackingFunction(WebViewerDrawingDisplayFailed);
  trackDrawingDownloaded = this.createTelemetryTrackingFunction(WebViewerDrawingDownloaded);
  trackDrawingDownloadFailed = this.createTelemetryTrackingFunction(WebViewerDrawingDownloadFailed);
  trackAnnotationPlaced = this.createTelemetryTrackingFunction(WebViewerAnnotationPlaced);
  trackCommentAdded = this.createTelemetryTrackingFunction(WebViewerCommentAdded);
  trackCommentSelected = this.createTelemetryTrackingFunction(WebViewerCommentSelected);
  trackViewLinkAccessDenied = this.createTelemetryTrackingFunction(WebViewerViewLinkAccessDenied);
  trackActiveToolChanged = this.createTelemetryTrackingFunction(WebViewerActiveToolChanged);
  trackCommentingDisplayed = this.createTelemetryTrackingFunction(WebViewerCommentingDisplayed);
  trackCommentingSidebarVisibilityChanged = this.createTelemetryTrackingFunction(
    WebViewerCommentingSidebarVisibilityChanged
  );
  trackCommentContextMenuVisibilityChanged = this.createTelemetryTrackingFunction(
    WebViewerCommentContextMenuVisibilityChanged
  );
  trackCommentDeleteIntended = this.createTelemetryTrackingFunction(WebViewerCommentDeleteIntended);
  trackCommentDeleteConfirmed = this.createTelemetryTrackingFunction(
    WebViewerCommentDeleteConfirmed
  );
  trackShareModalGeneralPermissionChanged = this.createTelemetryTrackingFunction(
    WebViewerShareModalGeneralPermissionChanged
  );
  trackShareModalGeneralRoleChanged = this.createTelemetryTrackingFunction(
    WebViewerShareModalGeneralRoleChanged
  );
  trackShareModalVisibilityChanged = this.createTelemetryTrackingFunction(
    WebShareModalVisibilityChanged
  );
  trackShareModalMemberRoleChanged = this.createTelemetryTrackingFunction(
    WebShareModalMemberRoleChanged
  );
  trackShareModalMemberRemoveIntended = this.createTelemetryTrackingFunction(
    WebShareModalMemberRemoveIntended
  );
  trackShareModalMemberRemoveConfirmed = this.createTelemetryTrackingFunction(
    WebShareModalMemberRemoveConfirmed
  );
  trackShareModalInvitePressed = this.createTelemetryTrackingFunction(WebShareModalInvitePressed);
  trackShareModalInviteRoleChanged = this.createTelemetryTrackingFunction(
    WebShareModalInviteRoleChanged
  );
  trackShareModalEmailRemoved = this.createTelemetryTrackingFunction(WebShareModalEmailRemoved);
  trackShareModalEmailAdded = this.createTelemetryTrackingFunction(WebShareModalEmailAdded);
  trackShareModalInvalidEmailAdded = this.createTelemetryTrackingFunction(
    WebShareModalInvalidEmailAdded
  );
  trackDashboardTeamSwitcherClicked = this.createTelemetryTrackingFunction(
    WebDashboardTeamSwitcherClicked
  );
  trackDashboardTeamSwitcherItemClicked = this.createTelemetryTrackingFunction(
    WebDashboardTeamSwitcherItemClicked
  );
  trackLinkClicked = this.createTelemetryTrackingFunction(WebLinkClicked);
  trackLinkContextMenuActed = this.createTelemetryTrackingFunction(WebLinkContextMenuActed);
  trackLinkContextMenuOpened = this.createTelemetryTrackingFunction(WebLinkContextMenuOpened);
  trackLinkCopied = this.createTelemetryTrackingFunction(WebLinkCopied);
  trackGoToCollaborationPromo = this.createTelemetryTrackingFunction(GoToCollaborationPromo);
  trackGoToMembers = this.createTelemetryTrackingFunction(GoToMembers);
  trackGoToSpace = this.createTelemetryTrackingFunction(GoToSpace);
  trackGoToTeam = this.createTelemetryTrackingFunction(GoToTeam);
  trackGoToDashboard = this.createTelemetryTrackingFunction(GoToDashboard);
  trackUnpublishConfirmed = this.createTelemetryTrackingFunction(WebLinkUnpublishConfirmed);
  trackDownloadFileClicked = this.createTelemetryTrackingFunction(ProjectPageDownloadFileClicked);
  trackProjectPageDownloadFileShown = this.createTelemetryTrackingFunction(
    ProjectPageDownloadFileShown
  );
  trackProjectPageLoaded = this.createTelemetryTrackingFunction(ProjectPageLoaded);
  trackProjectPageAccessRequested = this.createTelemetryTrackingFunction(
    ProjectPageAccessRequested
  );
  trackProjectPageAccessRequestAccepted = this.createTelemetryTrackingFunction(
    ProjectPageAccessRequestAccepted
  );
  trackProjectPageAccessRequestRejected = this.createTelemetryTrackingFunction(
    ProjectPageAccessRequestRejected
  );
  trackProjectPageAccessSwitchUser = this.createTelemetryTrackingFunction(
    ProjectPageAccessSwitchUser
  );
  trackComplexModelWarningShown = this.createTelemetryTrackingFunction(
    WebViewerComplexModelWarningShown
  );

  // Project sidebar events
  trackProjectSidebarToggled = this.createTelemetryTrackingFunction(ProjectSidebarToggled);
  trackVersionSelectorVisibilityChanged = this.createTelemetryTrackingFunction(
    VersionSelectorVisibilityChanged
  );

  // Deep link events
  trackDeeplinkButtonShown = this.createTelemetryTrackingFunction(DeeplinkButtonShown);
  trackDeeplinkAutoOpenToggled = this.createTelemetryTrackingFunction(DeeplinkAutoOpenToggled);
  trackDeeplinkButtonClicked = this.createTelemetryTrackingFunction(DeeplinkButtonClicked);
  trackDeeplinkGoToDownload = this.createTelemetryTrackingFunction(DeeplinkGoToDownload);
  trackDeeplinkModalClosed = this.createTelemetryTrackingFunction(DeeplinkModalClosed);
  trackDeeplinkModalOpened = this.createTelemetryTrackingFunction(DeeplinkModalOpened);
  trackDeeplinkOpenInitiated = this.createTelemetryTrackingFunction(DeeplinkOpenInitiated);
  trackDeeplinkRetryButtonClicked = this.createTelemetryTrackingFunction(
    DeeplinkRetryButtonClicked
  );

  // Paywall events
  trackPaywallModalShown = this.createTelemetryTrackingFunction(PaywallModalShown);
  trackPaywallModalDismissed = this.createTelemetryTrackingFunction(PaywallModalDismissed);
  trackPaywallSubscribeClicked = this.createTelemetryTrackingFunction(PaywallSubscribeClicked);

  trackProjectPageFeatureTourStepShown = this.createTelemetryTrackingFunction(
    ProjectPageFeatureTourStepShown
  );
  trackProjectPageFeatureTourCompleted = this.createTelemetryTrackingFunction(
    ProjectPageFeatureTourCompleted
  );
  trackProjectPageFeatureTourClose = this.createTelemetryTrackingFunction(
    ProjectPageFeatureTourClose
  );

  trackIntentPageLoaded = this.createTelemetryTrackingFunction(IntentPageLoaded);
  trackIntentPageFinished = this.createTelemetryTrackingFunction(IntentPageFinished);
  trackVideoStarted = this.createTelemetryTrackingFunction(VideoStarted);
  trackVideoStopped = this.createTelemetryTrackingFunction(VideoStopped);

  trackWelcomeModalShown = this.createTelemetryTrackingFunction(WelcomeModalShown);
  trackWelcomeModalClosed = this.createTelemetryTrackingFunction(WelcomeModalClosed);

  trackContextMenuOpened = this.createTelemetryTrackingFunction(ContextMenuOpened);
  trackContextMenuActed = this.createTelemetryTrackingFunction(ContextMenuActed);
  trackPriceShown = this.createTelemetryTrackingFunction(PriceShown);
}

export const trackingAdapter = new TrackingAdapter(
  new TelemetryClient({
    appName: "webapp",
    siteVersion: version,
    environment: telemetryClientEnvironment,
    telemetryUrlOverride: telemetryClientUrlOverride,
    titleOverride: "Shapr3D",
  })
);
